// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useMemo, useState } from "react"
import { Info } from "lucide-react"
import { Field } from "../Field"
import { Checkbox } from "../Checkbox"
import { RequiredMark } from "../RequiredMark"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { formatKcal, useI18n } from "@/i18n"
import {
  calorieFloor,
  estimateCalorieTarget,
  type ActivityPreviewInput,
  type GoalType,
} from "../calorieTarget"
import type { AnswerDraft, CalorieTargetDraft } from "../types"
import { ActivitySection } from "./ActivitySection"

const DEFAULT_ACTIVITY: ActivityPreviewInput = {
  level: null,
  sports: [],
  wearableKcalEnabled: false,
  avgDeviceActiveKcal: null,
}

interface Props {
  value: AnswerDraft
  onChange: (patch: AnswerDraft) => void
  goalType: GoalType | null
  initialCalorieTarget: CalorieTargetDraft | null
  onTargetDraftChange: (draft: CalorieTargetDraft | null) => void
}

/** Step 4 (wire) — body inputs and the calculated/manual target review
 * (below-floor handling lives in the parent flow via `onTargetDraftChange`,
 * see OnboardingPage's handleContinue).
 *
 * Owner decision 2026-08-28: sex/age/height/weight are simply REQUIRED — no
 * "prefer not to answer" escape. They are the literal inputs of the
 * maintenance calculation; a missing value used to leave targets unset and
 * plan generation failing closed (PROFILE_TARGETS_UNAVAILABLE), a dead end
 * dressed up as a choice. The wire schema keeps `preferNotToAnswer` for
 * stored legacy answers; new submissions always send an empty list, and the
 * schema's null-without-token rule keeps Continue disabled until every
 * metric is filled. Target weight stays genuinely optional. */
export function Step4Body({
  value,
  onChange,
  goalType,
  initialCalorieTarget,
  onTargetDraftChange,
}: Props) {
  const { t, locale } = useI18n()
  const sex = (value.sex as "female" | "male" | null | undefined) ?? null
  const age = (value.age as number | null | undefined) ?? null
  const heightCm = (value.heightCm as number | null | undefined) ?? null
  const weightKg = (value.weightKg as number | null | undefined) ?? null
  const targetWeightKg = (value.targetWeightKg as number | null | undefined) ?? null
  const activity = (value.activity as ActivityPreviewInput | undefined) ?? DEFAULT_ACTIVITY

  const [method, setMethod] = useState<"calculated" | "manual">(
    initialCalorieTarget?.source ?? "calculated"
  )
  const [manualKcal, setManualKcal] = useState<string>(
    initialCalorieTarget?.source === "manual" ? String(initialCalorieTarget.kcal) : ""
  )
  const [manualAck, setManualAck] = useState(false)

  function base() {
    return {
      sex,
      age,
      heightCm,
      weightKg,
      targetWeightKg,
      preferNotToAnswer: [],
      activity,
    }
  }

  function patch(field: string, raw: string) {
    const num = raw === "" ? null : Number(raw)
    onChange({ ...base(), [field]: Number.isFinite(num) ? num : null })
  }

  function updateActivity(nextActivity: ActivityPreviewInput) {
    onChange({ ...base(), activity: nextActivity })
  }

  const bmrInputs = useMemo(
    () => ({ sex, age, heightCm, weightKg }),
    [sex, age, heightCm, weightKg]
  )

  const estimate = useMemo(
    () => estimateCalorieTarget(bmrInputs, goalType ?? "maintain", activity),
    [bmrInputs, goalType, activity]
  )
  const floorKcal = calorieFloor(bmrInputs)

  useEffect(() => {
    if (method === "calculated") {
      if (estimate.rawKcal === null) {
        onTargetDraftChange(null)
        return
      }
      onTargetDraftChange({
        kcal: estimate.rawKcal,
        source: "calculated",
        floorAcknowledged: false,
      })
    } else {
      const parsed = Number(manualKcal)
      if (manualKcal === "" || !Number.isFinite(parsed)) {
        onTargetDraftChange(null)
        return
      }
      onTargetDraftChange({ kcal: parsed, source: "manual", floorAcknowledged: manualAck })
    }
  }, [method, estimate.rawKcal, manualKcal, manualAck, onTargetDraftChange])

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-baseline gap-1">
            <span className="text-sm font-medium text-ink">{t("onboarding.step4.sexLabel")}</span>
            <RequiredMark />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              aria-pressed={sex === "female"}
              onClick={() => onChange({ ...base(), sex: "female" })}
              className={`min-h-11 flex-1 rounded-bb-md border px-3 py-2 text-sm ${sex === "female" ? "border-green bg-green/10" : "border-line bg-paper"}`}
            >
              {t("onboarding.step4.sexFemale")}
            </button>
            <button
              type="button"
              aria-pressed={sex === "male"}
              onClick={() => onChange({ ...base(), sex: "male" })}
              className={`min-h-11 flex-1 rounded-bb-md border px-3 py-2 text-sm ${sex === "male" ? "border-green bg-green/10" : "border-line bg-paper"}`}
            >
              {t("onboarding.step4.sexMale")}
            </button>
          </div>
        </div>

        <Field label={t("onboarding.step4.ageLabel")} htmlFor="ob-age">
          <Input
            id="ob-age"
            type="number"
            min={18}
            max={100}
            value={age ?? ""}
            onChange={e => patch("age", e.target.value)}
          />
        </Field>

        <Field label={t("onboarding.step4.heightLabel")} htmlFor="ob-height">
          <Input
            id="ob-height"
            type="number"
            min={120}
            max={230}
            value={heightCm ?? ""}
            onChange={e => patch("heightCm", e.target.value)}
          />
        </Field>

        <Field label={t("onboarding.step4.weightLabel")} htmlFor="ob-weight">
          <Input
            id="ob-weight"
            type="number"
            min={35}
            max={300}
            step={0.1}
            value={weightKg ?? ""}
            onChange={e => patch("weightKg", e.target.value)}
          />
        </Field>

        <Field
          label={t("onboarding.step4.targetWeightLabel")}
          htmlFor="ob-target-weight"
          required={false}
        >
          <Input
            id="ob-target-weight"
            type="number"
            min={35}
            max={300}
            step={0.1}
            value={targetWeightKg ?? ""}
            onChange={e => patch("targetWeightKg", e.target.value)}
          />
        </Field>
      </div>

      {/* ADR-027 rule 7: activity/energy capture, ABOVE the target-calculation
          section below since the calculated target depends on it. */}
      <ActivitySection value={activity} onChange={updateActivity} />

      {/* Item 31: "calculated" is the prominent, default-selected path —
          a highlighted recommended card — with manual entry demoted to a
          small secondary toggle underneath, instead of two equal-weight
          buttons. The below-floor correction flow (BlockerScreen,
          proposeCalorieTarget/acceptFloorTarget) is untouched — only this
          framing changed. */}
      <div className="flex flex-col gap-3">
        <span className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t("onboarding.step4.methodLabel")}
        </span>

        <div
          className={cn(
            "flex flex-col gap-2 rounded-bb-xl border-2 p-5 transition-colors",
            method === "calculated" ? "border-green bg-green/5" : "border-line bg-paper"
          )}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-lime px-2.5 py-0.5 text-[10px] font-bold tracking-wide text-ink uppercase">
                {t("onboarding.step4.recommendedBadge")}
              </span>
              <span className="text-sm font-semibold text-ink">
                {t("onboarding.step4.methodCalculated")}
              </span>
            </div>
            {method === "manual" && (
              <button
                type="button"
                onClick={() => setMethod("calculated")}
                className="text-xs font-medium text-green underline underline-offset-2 hover:text-green-2"
              >
                {t("onboarding.step4.useCalculatedCta")}
              </button>
            )}
          </div>
          <p className="text-sm text-ink-2">{t("onboarding.step4.calculatedIntro")}</p>
          {estimate.rawKcal !== null ? (
            <p className="text-base font-bold text-ink">
              {t("onboarding.step4.reviewBody", { kcal: formatKcal(estimate.rawKcal, locale) })}
            </p>
          ) : (
            <p className="text-sm text-ink-2">{t("onboarding.step4.reviewTitle")}</p>
          )}
          <p className="text-xs text-ink-2">{t("onboarding.step4.floorNote")}</p>
        </div>

        {method === "manual" ? (
          <div className="flex flex-col gap-2 rounded-bb-md border border-line bg-paper p-4">
            <Field label={t("onboarding.step4.manualLabel")} htmlFor="ob-manual-kcal">
              <Input
                id="ob-manual-kcal"
                type="number"
                min={800}
                max={5000}
                step={10}
                value={manualKcal}
                onChange={e => {
                  setManualKcal(e.target.value)
                  setManualAck(false)
                }}
              />
            </Field>
            <p className="text-xs text-ink-2">{t("onboarding.step4.floorNote")}</p>
            {manualKcal !== "" && Number(manualKcal) >= floorKcal && (
              <label className="flex items-start gap-2 text-sm text-ink">
                <Checkbox id="ob-manual-ack" checked={manualAck} onCheckedChange={setManualAck} />
                <span>{t("onboarding.correction.acknowledgeFloorLabel")}</span>
              </label>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setMethod("manual")}
            className="self-start text-xs font-medium text-ink-2 underline underline-offset-2 hover:text-ink"
          >
            {t("onboarding.step4.methodManual")}
          </button>
        )}
      </div>

      {/* ADR-027 rule 6: the estimate/no-guarantee disclaimer sits next to
          the target itself, regardless of calculated vs. manual method —
          quiet, dignified treatment matching Step6Allergens' safety notice. */}
      <div className="flex gap-3 rounded-bb-lg border border-line bg-cream p-4">
        <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-ink-2" />
        <div className="flex flex-col gap-1 text-sm">
          <p className="font-semibold text-ink">{t("onboarding.step4.expectationTitle")}</p>
          <p className="text-ink-2">{t("onboarding.step4.expectationBody")}</p>
        </div>
      </div>
    </div>
  )
}
